import React, { useState } from "react";
import {
  Button,
  Input,
  Empty,
  Spin,
  message,
  Upload,
  Image,
} from "antd";
import { ArrowLeftOutlined, PaperClipOutlined } from "@ant-design/icons";
import {
  useGetMyTicketsQuery,
  useGetMyTicketByIdQuery,
  useReplyToMyTicketMutation,
} from "api/supportApi";
import uploadSupportFileToS3, {
  validateSupportFile,
} from "utils/supportS3Upload";
import SupportIntakeModal from "@/components/SupportIntakeModal";
import {
  Wrap,
  HeaderRow,
  NewRequestBtn,
  TicketCard,
  CardBody,
  CardRef,
  CardMeta,
  CardPreview,
  BadgeColumn,
  StatusPill,
  MetaRow,
  SectionTitle,
  Thread,
  Bubble,
  ReplyBox,
  AttachGrid,
  DetailTitle,
  DetailDescription,
  BackLink,
  EmptyHint,
  ReplyActions,
  SendBtn,
  DetailCard,
  DetailCardTitle,
  MetaDetailRow,
} from "./styles";

const { TextArea } = Input;

const META_LABELS = {
  platform: "Platform",
  invoiceOrOrder: "Invoice / order number",
  accountEmail: "Account email",
  importance: "Importance",
};

const STATUS_STYLES = {
  OPEN: { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" },
  IN_PROGRESS: { bg: "#ffedd5", color: "#c2410c", border: "#fed7aa" },
  WAITING_ON_CUSTOMER: { bg: "#fef3c7", color: "#b45309", border: "#fde68a" },
  RESOLVED: { bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
  CLOSED: { bg: "#f3f4f6", color: "#4b5563", border: "#e5e7eb" },
  PENDING: { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" },
  SCHEDULED: { bg: "#ffedd5", color: "#c2410c", border: "#fed7aa" },
  COMPLETED: { bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
  CANCELLED: { bg: "#f3f4f6", color: "#4b5563", border: "#e5e7eb" },
};

const PRIORITY_STYLES = {
  Low: { bg: "#f3f4f6", color: "#4b5563", border: "#e5e7eb" },
  Medium: { bg: "#fef3c7", color: "#b45309", border: "#fde68a" },
  High: { bg: "#ffedd5", color: "#c2410c", border: "#fed7aa" },
  Urgent: { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
};

const formatStatus = (status) =>
  String(status || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatCategory = (cat) =>
  String(cat || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.CLOSED;
  return (
    <StatusPill $bg={s.bg} $color={s.color} $border={s.border}>
      {formatStatus(status)}
    </StatusPill>
  );
};

const PriorityBadge = ({ priority }) => {
  const s = PRIORITY_STYLES[priority] || PRIORITY_STYLES.Medium;
  return (
    <StatusPill $bg={s.bg} $color={s.color} $border={s.border}>
      {priority}
    </StatusPill>
  );
};

const Attachments = ({ items }) => {
  if (!items?.length) return null;
  return (
    <AttachGrid>
      {items.map((a) =>
        a.mimeType?.startsWith("image/") ? (
          <Image key={a.id || a.url} src={a.url} alt={a.fileName} width={80} />
        ) : (
          <a key={a.id || a.url} href={a.url} target="_blank" rel="noreferrer">
            <PaperClipOutlined /> {a.fileName}
          </a>
        )
      )}
    </AttachGrid>
  );
};

const TicketDetail = ({ ticketId, onBack }) => {
  const { data: ticket, isLoading } = useGetMyTicketByIdQuery(ticketId);
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sendReply, { isLoading: sending }] = useReplyToMyTicketMutation();

  const closed =
    ticket?.status === "CLOSED" || ticket?.status === "RESOLVED";

  const handleUpload = async (file) => {
    const err = validateSupportFile(file);
    if (err) {
      message.error(err);
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    try {
      const uploaded = await uploadSupportFileToS3(file);
      setAttachments((prev) => [...prev, uploaded]);
    } catch (e) {
      message.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleSend = async () => {
    if (!reply.trim()) return message.error("Enter a reply");
    try {
      await sendReply({
        id: ticketId,
        body: {
          body: reply.trim(),
          attachments: attachments.length ? attachments : undefined,
        },
      }).unwrap();
      setReply("");
      setAttachments([]);
      message.success("Reply sent");
    } catch (e) {
      message.error(e?.data?.message || "Failed to send reply");
    }
  };

  if (isLoading) {
    return (
      <Wrap>
        <Spin />
      </Wrap>
    );
  }
  if (!ticket) {
    return (
      <Wrap>
        <Empty description="Ticket not found" />
      </Wrap>
    );
  }

  return (
    <Wrap>
      <BackLink type="button" onClick={onBack}>
        <ArrowLeftOutlined /> Back to list
      </BackLink>

      <DetailTitle>{ticket.referenceNumber}</DetailTitle>
      <MetaRow>
        <StatusBadge status={ticket.status} />
        <PriorityBadge priority={ticket.priority} />
        <StatusPill $bg="#f9fafb" $color="#374151" $border="#e5e7eb">
          {formatCategory(ticket.category)}
        </StatusPill>
      </MetaRow>

      <DetailDescription>{ticket.description}</DetailDescription>
      <Attachments items={ticket.initialAttachments || []} />

      {ticket.metadata && Object.keys(ticket.metadata).length > 0 && (
        <DetailCard>
          <DetailCardTitle>Additional details</DetailCardTitle>
          {Object.entries(ticket.metadata).map(([key, value]) => (
            <MetaDetailRow key={key}>
              <span>{META_LABELS[key] || key}</span>
              <span>{String(value)}</span>
            </MetaDetailRow>
          ))}
        </DetailCard>
      )}

      <SectionTitle>Conversation</SectionTitle>
      <Thread>
        {(ticket.messages || []).length === 0 && (
          <EmptyHint>No replies yet. We'll get back to you soon.</EmptyHint>
        )}
        {(ticket.messages || []).map((m) => (
          <Bubble key={m.id} $mine={m.authorType === "USER"}>
            <div className="meta">
              {m.authorType === "USER" ? "You" : "Support"} ·{" "}
              {new Date(m.createdAt).toLocaleString()}
            </div>
            <div>{m.body}</div>
            <Attachments items={m.attachments} />
          </Bubble>
        ))}
      </Thread>

      {!closed && (
        <ReplyBox>
          <TextArea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a reply…"
          />
          <ReplyActions>
            <Upload
              beforeUpload={handleUpload}
              fileList={attachments.map((a, i) => ({
                uid: String(i),
                name: a.fileName,
                status: "done",
              }))}
              onRemove={(f) =>
                setAttachments((prev) =>
                  prev.filter((a) => a.fileName !== f.name)
                )
              }
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
              maxCount={5}
            >
              <Button icon={<PaperClipOutlined />} disabled={uploading}>
                Attach
              </Button>
            </Upload>
            <SendBtn type="button" onClick={handleSend} disabled={sending}>
              {sending ? "Sending…" : "Send reply"}
            </SendBtn>
          </ReplyActions>
        </ReplyBox>
      )}
    </Wrap>
  );
};

const MySupport = () => {
  const [selectedId, setSelectedId] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const { data, isLoading, isError } = useGetMyTicketsQuery({
    page: 1,
    limit: 50,
  });

  if (selectedId) {
    return (
      <TicketDetail ticketId={selectedId} onBack={() => setSelectedId(null)} />
    );
  }

  return (
    <Wrap>
      <HeaderRow>
        <h2>My Support</h2>
        <NewRequestBtn type="button" onClick={() => setSupportOpen(true)}>
          New request
        </NewRequestBtn>
      </HeaderRow>

      {isLoading && <Spin />}
      {isError && <Empty description="Could not load tickets" />}

      <SectionTitle>Tickets</SectionTitle>
      {!isLoading && !(data?.tickets?.length) && (
        <Empty description="No tickets yet" style={{ marginBottom: 16 }} />
      )}
      {(data?.tickets || []).map((t) => (
        <TicketCard
          key={t.id}
          $clickable
          onClick={() => setSelectedId(t.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setSelectedId(t.id);
          }}
        >
          <CardBody>
            <CardRef>{t.referenceNumber}</CardRef>
            <CardMeta>
              {formatCategory(t.category)} ·{" "}
              {new Date(t.createdAt).toLocaleDateString()}
            </CardMeta>
            <CardPreview>{t.description}</CardPreview>
          </CardBody>
          <BadgeColumn>
            <StatusBadge status={t.status} />
            <PriorityBadge priority={t.priority} />
          </BadgeColumn>
        </TicketCard>
      ))}

      <SectionTitle>Callbacks</SectionTitle>
      {!isLoading && !(data?.callbacks?.length) && (
        <Empty description="No callback requests" />
      )}
      {(data?.callbacks || []).map((c) => (
        <TicketCard key={c.id} $clickable={false}>
          <CardBody>
            <CardRef>{c.referenceNumber}</CardRef>
            <CardMeta>
              {c.phone} · {c.timing === "SCHEDULED" ? "Scheduled" : "ASAP"}
              {c.scheduledSlot ? ` · ${c.scheduledSlot}` : ""}
            </CardMeta>
            {c.topic && <CardPreview>{c.topic}</CardPreview>}
          </CardBody>
          <BadgeColumn>
            <StatusBadge status={c.status} />
          </BadgeColumn>
        </TicketCard>
      ))}

      <SupportIntakeModal
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
      />
    </Wrap>
  );
};

export default MySupport;
